<template>
    <section class="preset-manager">
        <div class="preset-manager__toolbar">
            <div>
                <h2>{{ $tt('快捷指令') }}</h2>
                <p>{{ $tt('把常用的批量操作保存为可重复使用的指令。') }}</p>
            </div>
            <button class="btn btn-primary btn-sm" type="button" @click="openCreate">
                <span aria-hidden="true">+</span>
                {{ $tt('新建指令') }}
            </button>
        </div>

        <div class="preset-manager__tabs">
            <button :class="{ active: sourceTab === 'all' }" type="button" @click="sourceTab = 'all'">
                {{ $tt('全部') }} <span>{{ presets.length }}</span>
            </button>
            <button :class="{ active: sourceTab === 'system' }" type="button" @click="sourceTab = 'system'">
                {{ $tt('系统指令') }}
            </button>
            <button :class="{ active: sourceTab === 'personal' }" type="button" @click="sourceTab = 'personal'">
                {{ $tt('我的指令') }}
            </button>
            <input v-model.trim="query" type="search" :placeholder="$tt('搜索快捷指令')" />
        </div>

        <div v-if="loading" class="preset-manager__empty">{{ $tt('正在加载快捷指令...') }}</div>
        <div v-else-if="filteredPresets.length === 0" class="preset-manager__empty">
            <strong>{{ $tt('还没有快捷指令') }}</strong>
            <span>{{ $tt('创建一条指令，把常用批量操作固定下来。') }}</span>
            <button class="btn btn-primary btn-sm" type="button" @click="openCreate">{{ $tt('新建指令') }}</button>
        </div>
        <div v-else class="preset-manager__grid">
            <article
                v-for="preset in filteredPresets"
                :key="preset.id"
                class="preset-card"
                :class="{ 'preset-card--inactive': preset.status !== 'active' }"
            >
                <div class="preset-card__header">
                    <div class="preset-card__icon">{{ preset.icon || '⌘' }}</div>
                    <div class="preset-card__title">
                        <h3>{{ preset.display_name }}</h3>
                        <span>{{ preset.scope === 'system' ? $tt('系统指令') : $tt('我的指令') }}</span>
                    </div>
                    <span
                        class="preset-card__status"
                        :class="preset.status === 'active' ? 'is-active' : 'is-inactive'"
                    >
                        {{ preset.status === 'active' ? $tt('已启用') : $tt('已停用') }}
                    </span>
                    <div class="dropdown dropdown-end" :class="{ 'dropdown-open': openMenuId === preset.id }">
                        <button
                            class="btn btn-ghost btn-xs btn-circle"
                            type="button"
                            :aria-expanded="openMenuId === preset.id"
                            :title="$tt('更多操作')"
                            @click.stop="togglePresetMenu(preset.id)"
                        >•••</button>
                        <ul v-if="openMenuId === preset.id" class="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-36">
                            <li><button type="button" @click="openExecutions(preset); closePresetMenu()">{{ $tt('执行记录') }}</button></li>
                            <li><button type="button" @click="copyPreset(preset); closePresetMenu()">{{ $tt('复制') }}</button></li>
                            <li><button type="button" @click="editPreset(preset); closePresetMenu()">{{ $tt('编辑') }}</button></li>
                            <li><button type="button" @click="togglePreset(preset); closePresetMenu()">{{ preset.status === 'active' ? $tt('停用') : $tt('启用') }}</button></li>
                            <li v-if="preset.scope !== 'system'"><button class="text-error" type="button" @click="requestRemovePreset(preset); closePresetMenu()">{{ $tt('删除') }}</button></li>
                        </ul>
                    </div>
                </div>
                <p class="preset-card__description">{{ preset.description || $tt('暂无描述') }}</p>
                <div class="preset-card__meta">
                    <span>{{ $tt('{count} 个步骤', { count: preset.step_count }) }}</span>
                    <span>{{ $tt('最近使用') }} {{ formatTime(preset.last_used_at) }}</span>
                </div>
                <div class="preset-card__steps">
                    <span v-for="(step, index) in preset.definition.steps.slice(0, 3)" :key="`${preset.id}-${index}`">
                        {{ operationLabel(step.operation) }}
                    </span>
                    <span v-if="preset.step_count > 3">+{{ preset.step_count - 3 }}</span>
                </div>
            </article>
        </div>

        <div v-if="editorVisible" class="drawer drawer-end">
            <input id="operation-preset-editor" v-model="editorVisible" type="checkbox" class="drawer-toggle" />
            <div class="drawer-side">
                <label for="operation-preset-editor" class="drawer-overlay" @click="closeEditor"></label>
                <div class="preset-editor">
                    <div class="preset-editor__header">
                        <div>
                            <h2>{{ editing ? $tt('编辑快捷指令') : $tt('新建快捷指令') }}</h2>
                            <p>{{ $tt('选择原子操作并配置固定参数。') }}</p>
                        </div>
                        <button class="btn btn-ghost btn-sm btn-circle" type="button" @click="closeEditor">×</button>
                    </div>
                    <div class="preset-editor__body">
                        <label class="form-control">
                            <span class="label-text">{{ $tt('显示名称') }}</span>
                            <input v-model.trim="form.display_name" class="input input-bordered input-sm" type="text" />
                        </label>
                        <label class="form-control">
                            <span class="label-text">{{ $tt('内部名称') }}</span>
                            <input v-model.trim="form.name" class="input input-bordered input-sm" type="text" />
                        </label>
                        <label class="form-control">
                            <span class="label-text">{{ $tt('描述') }}</span>
                            <textarea v-model.trim="form.description" class="textarea textarea-bordered" rows="2"></textarea>
                        </label>
                        <div class="preset-runtime-section">
                            <div class="preset-editor__section-title">
                                <strong>{{ $tt('运行时参数') }}</strong>
                                <button class="btn btn-outline btn-xs" type="button" @click="addRuntimeParam">
                                    {{ $tt('添加参数') }}
                                </button>
                            </div>
                            <p class="preset-runtime-section__hint">
                                {{ $tt('可将步骤中的部分值设置为执行时填写。没有运行时参数时，指令可直接执行。') }}
                            </p>
                            <div v-for="(field, index) in form.input_schema" :key="field.key_id" class="preset-runtime-field">
                                <div class="preset-runtime-field__top">
                                    <input v-model.trim="field.key" class="input input-bordered input-sm" type="text" :placeholder="$tt('参数 key，例如 replace_text')" />
                                    <input v-model.trim="field.label" class="input input-bordered input-sm" type="text" :placeholder="$tt('显示名称')" />
                                    <button class="btn btn-ghost btn-xs btn-circle" type="button" :title="$tt('删除参数')" @click="removeRuntimeParam(index)">×</button>
                                </div>
                                <div class="preset-runtime-field__bottom">
                                    <select v-model="field.type" class="select select-bordered select-sm">
                                        <option value="string">{{ $tt('文本') }}</option>
                                        <option value="path">{{ $tt('路径') }}</option>
                                        <option value="number">{{ $tt('数字') }}</option>
                                        <option value="boolean">{{ $tt('开关') }}</option>
                                        <option value="select">{{ $tt('选项') }}</option>
                                    </select>
                                    <input v-model="field.default" class="input input-bordered input-sm" type="text" :placeholder="$tt('默认值')" />
                                    <label class="preset-runtime-field__required">
                                        <input v-model="field.required" type="checkbox" class="checkbox checkbox-sm" />
                                        {{ $tt('必填') }}
                                    </label>
                                </div>
                                <input v-model.trim="field.description" class="input input-bordered input-sm w-full" type="text" :placeholder="$tt('参数说明，可选')" />
                                <input v-if="field.type === 'select'" v-model="field.options_text" class="input input-bordered input-sm w-full" type="text" :placeholder="$tt('选项，用逗号分隔')" />
                            </div>
                        </div>
                        <div class="preset-editor__section-title">
                            <strong>{{ $tt('操作步骤') }}</strong>
                            <button class="btn btn-outline btn-xs" type="button" @click="addStep">{{ $tt('添加步骤') }}</button>
                        </div>
                        <div v-for="(step, index) in form.definition.steps" :key="step.key" class="preset-step">
                            <div class="preset-step__header">
                                <span>{{ index + 1 }}</span>
                                <select v-model="step.operation" class="select select-bordered select-sm" @change="resetStepParams(step)">
                                    <option v-for="operation in operations" :key="operation.id" :value="operation.id">
                                        {{ operation.display_name }}
                                    </option>
                                </select>
                                <button class="btn btn-ghost btn-xs btn-circle" type="button" :disabled="index === 0" :title="$tt('上移')" @click="moveStep(index, -1)">↑</button>
                                <button class="btn btn-ghost btn-xs btn-circle" type="button" :disabled="index === form.definition.steps.length - 1" :title="$tt('下移')" @click="moveStep(index, 1)">↓</button>
                                <button class="btn btn-ghost btn-xs btn-circle" type="button" @click="removeStep(index)">×</button>
                            </div>
                            <operation-step-form :step="step" :input-schema="form.input_schema" />
                        </div>
                    </div>
                    <div class="preset-editor__footer">
                        <button class="btn btn-outline btn-sm" type="button" @click="closeEditor">{{ $tt('取消') }}</button>
                        <button class="btn btn-primary btn-sm" type="button" :disabled="saving" @click="savePreset">
                            <span v-if="saving" class="loading loading-spinner loading-xs"></span>
                            {{ $tt('保存指令') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="executionDrawerVisible" class="drawer drawer-end">
            <input id="operation-preset-executions" v-model="executionDrawerVisible" type="checkbox" class="drawer-toggle" />
            <div class="drawer-side">
                <label for="operation-preset-executions" class="drawer-overlay" @click="closeExecutions"></label>
                <div class="preset-executions">
                    <div class="preset-executions__header">
                        <div>
                            <h2>{{ $tt('执行记录') }}</h2>
                            <p>{{ executionPreset ? executionPreset.display_name : '' }}</p>
                        </div>
                        <button class="btn btn-ghost btn-sm btn-circle" type="button" @click="closeExecutions">×</button>
                    </div>
                    <div class="preset-executions__body">
                        <div v-if="executionLoading" class="preset-manager__empty">{{ $tt('正在加载执行记录...') }}</div>
                        <div v-else-if="executions.length === 0" class="preset-manager__empty">
                            <strong>{{ $tt('暂无执行记录') }}</strong>
                        </div>
                        <article v-for="execution in executions" v-else :key="execution.id" class="preset-execution">
                            <div class="preset-execution__top">
                                <strong>{{ statusLabel(execution.status) }}</strong>
                                <span>{{ formatTime(execution.created_at) }}</span>
                            </div>
                            <div class="preset-execution__counts">
                                <span>{{ $tt('成功') }} {{ execution.success_count }}</span>
                                <span>{{ $tt('跳过') }} {{ execution.skipped_count }}</span>
                                <span>{{ $tt('失败') }} {{ execution.failed_count }}</span>
                                <span>{{ $tt('总计') }} {{ execution.total_count }}</span>
                            </div>
                            <p v-if="execution.error_message" class="preset-execution__error">{{ execution.error_message }}</p>
                            <details>
                                <summary>{{ $tt('快照与参数') }}</summary>
                                <pre>{{ formatExecutionDetail(execution) }}</pre>
                            </details>
                        </article>
                    </div>
                </div>
            </div>
        </div>

        <dialog ref="deletePresetModal" class="modal modal-bottom sm:modal-middle" @close="presetToDelete = null">
            <div class="modal-box">
                <h3 class="font-bold text-lg text-error">{{ $tt('删除快捷指令') }}</h3>
                <div class="py-4">
                    <p>
                        {{ $tt('确定删除“{name}”吗？', { name: presetToDelete?.display_name || '' }) }}
                    </p>
                    <div class="alert alert-warning mt-4 text-sm">
                        <span>{{ $tt('删除后将无法恢复。') }}</span>
                    </div>
                </div>
                <div class="modal-action">
                    <button class="btn btn-outline btn-sm" type="button" :disabled="deletingPreset" @click="closeDeletePresetDialog">
                        {{ $tt('取消') }}
                    </button>
                    <button class="btn btn-error btn-sm" type="button" :disabled="deletingPreset" @click="confirmRemovePreset">
                        <span v-if="deletingPreset" class="loading loading-spinner loading-xs"></span>
                        {{ $tt('删除') }}
                    </button>
                </div>
            </div>
            <form method="dialog" class="modal-backdrop">
                <button :disabled="deletingPreset">{{ $tt('关闭') }}</button>
            </form>
        </dialog>
    </section>
</template>

<script>
import OperationStepForm from '@/components/batch/OperationStepForm.vue'
import { BATCH_OPERATION_CATALOG, TAG_FIELDS, UPDATE_TAG_ACTIONS, DEFAULT_TIDY_RULE_FORM, createOperationParams, getBatchOperationDefinition } from '@/components/batch/batchOperationCatalog'

export default {
    name: 'OperationPresetManager',
    components: { OperationStepForm },
    data() {
        return {
            presets: [],
            operations: [],
            sourceTab: 'all',
            query: '',
            loading: false,
            saving: false,
            editorVisible: false,
            executionDrawerVisible: false,
            executionLoading: false,
            executionPreset: null,
            executions: [],
            openMenuId: null,
            presetToDelete: null,
            deletingPreset: false,
            editing: null,
            form: this.createForm()
        }
    },
    computed: {
        filteredPresets() {
            const query = this.query.toLocaleLowerCase()
            return this.presets.filter(item => {
                const sourceMatched = this.sourceTab === 'all' || item.scope === this.sourceTab
                const text = [item.display_name, item.description].filter(Boolean).join(' ').toLocaleLowerCase()
                return sourceMatched && (!query || text.includes(query))
            })
        }
    },
    created() {
        this.load()
    },
    methods: {
        createForm() {
            return {
                name: '',
                display_name: '',
                description: '',
                category: 'file',
                icon: '⌘',
                scope: 'personal',
                risk_level: 'medium',
                input_schema: [],
                definition: { schema_version: 1, steps: [] }
            }
        },
        async load() {
            this.loading = true
            try {
                const [presetResponse, operationResponse] = await Promise.all([
                    this.$api.scriptMgmt.getOperationPresets({ page_size: 100 }),
                    this.$api.scriptMgmt.getOperationDefinitions()
                ])
                this.presets = presetResponse.data.items || presetResponse.data || []
                const apiOperations = operationResponse.data || []
                const apiIds = new Set(apiOperations.map(item => item.id))
                this.operations = apiOperations.concat(
                    BATCH_OPERATION_CATALOG.filter(item => !apiIds.has(item.id)).map(item => ({
                        ...item,
                        description: item.display_name,
                        params: Object.fromEntries((item.params || []).map(param => [param.key, {
                            type: param.type,
                            default: param.default,
                            options: param.options || []
                        }]))
                    }))
                )
            } catch (error) {
                this.notify('获取快捷指令失败', error.message, 'error')
            } finally {
                this.loading = false
            }
        },
        openCreate() {
            this.editing = null
            this.form = this.createForm()
            this.addStep()
            this.editorVisible = true
        },
        editPreset(preset) {
            this.editing = preset
            this.form = JSON.parse(JSON.stringify(preset))
            this.form.input_schema = (this.form.input_schema || []).map(field => ({
                key_id: `${Date.now()}-${Math.random()}`,
                key: field.key || '',
                label: field.label || '',
                type: field.type || 'string',
                required: !!field.required,
                default: field.default ?? '',
                description: field.description || '',
                options_text: (field.options || []).join(', ')
            }))
            this.form.definition.steps.forEach(step => {
                this.$set(step, 'key', `${Date.now()}-${Math.random()}`)
                this.normalizeStep(step)
            })
            this.editorVisible = true
        },
        closeEditor() {
            this.editorVisible = false
        },
        addStep() {
            const operation = this.operations[0]?.id || BATCH_OPERATION_CATALOG[0]?.id
            const step = {
                key: `${Date.now()}-${Math.random()}`,
                operation,
                params: {}
            }
            this.resetStepParams(step)
            this.form.definition.steps.push(step)
        },
        removeStep(index) {
            this.form.definition.steps.splice(index, 1)
        },
        moveStep(index, offset) {
            const targetIndex = index + offset
            if (targetIndex < 0 || targetIndex >= this.form.definition.steps.length) return
            const steps = this.form.definition.steps
            const current = steps.splice(index, 1)[0]
            steps.splice(targetIndex, 0, current)
        },
        addRuntimeParam() {
            const usedKeys = new Set((this.form.input_schema || []).map(field => field.key))
            let index = this.form.input_schema.length + 1
            let key = `param_${index}`
            while (usedKeys.has(key)) {
                index += 1
                key = `param_${index}`
            }
            this.form.input_schema.push({
                key_id: `${Date.now()}-${Math.random()}`,
                key,
                label: '',
                type: 'string',
                required: true,
                default: '',
                description: '',
                options_text: ''
            })
        },
        removeRuntimeParam(index) {
            const field = this.form.input_schema[index]
            const key = field && field.key
            this.form.input_schema.splice(index, 1)
            if (!key) return
            this.form.definition.steps.forEach(step => {
                if (step.params.template_runtime_key === key) this.$set(step.params, 'template_runtime_key', '')
                if (step.params.before_runtime_key === key) this.$set(step.params, 'before_runtime_key', '')
                if (step.params.after_runtime_key === key) this.$set(step.params, 'after_runtime_key', '')
                if (step.params.root_path_runtime_key === key) this.$set(step.params, 'root_path_runtime_key', '')
                ;(step.params.replace_text_list || []).forEach(rule => {
                    if (rule.before_runtime_key === key) this.$set(rule, 'before_runtime_key', '')
                    if (rule.after_runtime_key === key) this.$set(rule, 'after_runtime_key', '')
                })
                Object.values(step.params.fields || {}).forEach(fieldValue => {
                    if (fieldValue.runtime_key === key) this.$set(fieldValue, 'runtime_key', '')
                })
            })
        },
        resetStepParams(step) {
            const defaults = {
                update_tags: { fields: {} },
                replace_text: { fields: ['title', 'artist', 'album'], before: '', before_runtime_key: '', after: '', after_runtime_key: '', mode: 'text' },
                convert_chinese: { fields: ['title', 'artist', 'album'], mode: 'trad2sim' },
                split_filename: { extract_field: 'filename', rule_list: [], rule_list_text: '' },
                tidy_folder: { root_path: '', root_path_runtime_key: '', dir_list: [{ value: 'artist', value2: '' }, { value: 'album', value2: '' }], rule_form: DEFAULT_TIDY_RULE_FORM, collision_policy: 'skip' }
            }
            const operationDefault = defaults[step.operation] || createOperationParams(step.operation)
            step.params = JSON.parse(JSON.stringify(operationDefault))
            this.normalizeStep(step)
        },
        createCatalogParams(operation) {
            const definition = getBatchOperationDefinition(operation)
            return (definition?.params || []).reduce((params, field) => {
                params[field.key] = JSON.parse(JSON.stringify(field.default))
                return params
            }, {})
        },
        normalizeStep(step) {
            if (step.operation === 'update_tags') {
                const current = step.params.fields || {}
                const fields = {}
                TAG_FIELDS.forEach(field => {
                    const currentValue = current[field.id]
                    if (currentValue && typeof currentValue === 'object' && 'enabled' in currentValue) {
                        fields[field.id] = {
                            enabled: !!currentValue.enabled,
                            value: currentValue.value ?? '',
                            runtime_key: currentValue.runtime_key || ''
                        }
                    } else if (currentValue && typeof currentValue === 'object' && currentValue.runtime_key) {
                        fields[field.id] = {
                            enabled: true,
                            value: '',
                            runtime_key: currentValue.runtime_key
                        }
                    } else {
                        fields[field.id] = {
                            enabled: currentValue !== undefined && currentValue !== null,
                            value: currentValue || '',
                            runtime_key: ''
                        }
                    }
                })
                UPDATE_TAG_ACTIONS.forEach(action => {
                    fields[action.id] = {
                        enabled: current[action.id] && typeof current[action.id] === 'object'
                            ? !!current[action.id].enabled
                            : !!current[action.id],
                        value: true,
                        runtime_key: ''
                    }
                })
                this.$set(step.params, 'fields', fields)
            }
            if (step.operation === 'replace_text') {
                const rules = Array.isArray(step.params.replace_text_list) && step.params.replace_text_list.length
                    ? step.params.replace_text_list
                    : [{ before: step.params.before || '', after: step.params.after || '' }]
                this.$set(step.params, 'replace_text_list', rules.map(rule => ({
                    before: typeof rule.before === 'object' ? '' : rule.before || '',
                    after: typeof rule.after === 'object' ? '' : rule.after || '',
                    before_runtime_key: rule.before && rule.before.runtime_key ? rule.before.runtime_key : '',
                    after_runtime_key: rule.after && rule.after.runtime_key ? rule.after.runtime_key : ''
                })))
            }
            if (step.operation === 'split_filename') {
                this.$set(step.params, 'rule_list_text', (step.params.rule_list || []).join('\n'))
            }
            if (step.operation === 'tidy_folder') {
                this.$set(step.params, 'root_path_runtime_key', step.params.root_path && step.params.root_path.runtime_key
                    ? step.params.root_path.runtime_key
                    : (step.params.root_path_runtime_key || ''))
                if (step.params.root_path && typeof step.params.root_path === 'object') this.$set(step.params, 'root_path', '')
                if (!Array.isArray(step.params.dir_list)) this.$set(step.params, 'dir_list', [{ value: 'artist', value2: '' }, { value: 'album', value2: '' }])
                this.$set(step.params, 'dir_list', step.params.dir_list.map(item => typeof item === 'string' ? { value: item, value2: '' } : { value: item.value || item.name || 'artist', value2: item.value2 || '' }))
                if (!step.params.rule_form || typeof step.params.rule_form !== 'object' || Array.isArray(step.params.rule_form)) this.$set(step.params, 'rule_form', {})
                Object.keys(DEFAULT_TIDY_RULE_FORM).forEach(key => {
                    if (step.params.rule_form[key] === undefined) this.$set(step.params.rule_form, key, JSON.parse(JSON.stringify(DEFAULT_TIDY_RULE_FORM[key])))
                })
            }
        },
        operationLabel(id) {
            return this.operations.find(item => item.id === id)?.display_name || id
        },
        riskLabel(level) {
            return { low: '低风险', medium: '中风险', high: '高风险' }[level] || level
        },
        statusLabel(status) {
            return {
                waiting: '等待执行',
                running: '执行中',
                success: '成功',
                partial: '部分成功',
                failed: '失败'
            }[status] || status
        },
        formatTime(value) {
            if (!value) return '-'
            const date = new Date(value)
            if (Number.isNaN(date.getTime())) return value
            return date.toLocaleString()
        },
        formatExecutionDetail(execution) {
            return JSON.stringify({
                runtime_params: execution.runtime_params || {},
                preset_snapshot: execution.preset_snapshot || {}
            }, null, 2)
        },
        async openExecutions(preset) {
            this.executionPreset = preset
            this.executionDrawerVisible = true
            this.executionLoading = true
            try {
                const response = await this.$api.scriptMgmt.getOperationPresetExecutions(preset.id)
                if (!response.result) throw new Error(response.message || '获取执行记录失败')
                this.executions = response.data || []
            } catch (error) {
                this.executions = []
                this.notify('获取执行记录失败', error.message, 'error')
            } finally {
                this.executionLoading = false
            }
        },
        closeExecutions() {
            this.executionDrawerVisible = false
        },
        togglePresetMenu(id) {
            this.openMenuId = this.openMenuId === id ? null : id
        },
        closePresetMenu() {
            this.openMenuId = null
        },
        serializeForm() {
            const data = JSON.parse(JSON.stringify(this.form))
            data.input_schema = (data.input_schema || []).map(field => ({
                key: String(field.key || '').trim(),
                label: String(field.label || '').trim(),
                type: field.type || 'string',
                required: !!field.required,
                default: field.default ?? '',
                description: String(field.description || '').trim(),
                options: field.type === 'select'
                    ? String(field.options_text || '').split(',').map(item => item.trim()).filter(Boolean)
                    : []
            }))
            data.definition.steps.forEach((step, index) => {
                const originalStep = this.form.definition.steps[index]
                delete step.key
                delete step.params.template_mode
                delete step.params.template_parts
                delete step.params.new_template_text
                delete step.params.template_runtime_key
                delete step.params.before_runtime_key
                delete step.params.after_runtime_key
                if (originalStep && originalStep.params.template_runtime_key) {
                    step.params.template = { runtime_key: originalStep.params.template_runtime_key }
                }
                if (originalStep && originalStep.params.before_runtime_key) {
                    step.params.before = { runtime_key: originalStep.params.before_runtime_key }
                }
                if (originalStep && originalStep.params.after_runtime_key) {
                    step.params.after = { runtime_key: originalStep.params.after_runtime_key }
                }
                if (step.operation === 'update_tags') {
                    const fields = {}
                    Object.keys(step.params.fields || {}).forEach(key => {
                        const field = step.params.fields[key]
                        if (field.enabled) {
                            fields[key] = field.runtime_key
                                ? { runtime_key: field.runtime_key }
                                : field.value
                        }
                    })
                    step.params.fields = fields
                }
                if (step.operation === 'split_filename') {
                    step.params.rule_list = (step.params.rule_list_text || '').split('\n').map(item => item.trim()).filter(Boolean)
                    delete step.params.rule_list_text
                }
            if (step.operation === 'tidy_folder') {
                delete step.params.root_path_runtime_key
                    if (originalStep.params.root_path_runtime_key) {
                    step.params.root_path = { runtime_key: originalStep.params.root_path_runtime_key }
                }
                step.params.dir_list = (step.params.dir_list || []).map(item => {
                    const value = item.value || item.name || 'artist'
                    return item.value2
                        ? { type: 'text', value: item.value2 }
                        : { type: 'field', name: value }
                })
                delete step.params.rule_form_text
            }
            if (step.operation === 'replace_text') {
                step.params.replace_text_list = (step.params.replace_text_list || []).map(rule => {
                    const item = { before: rule.before || '', after: rule.after || '' }
                    if (rule.before_runtime_key) item.before = { runtime_key: rule.before_runtime_key }
                    if (rule.after_runtime_key) item.after = { runtime_key: rule.after_runtime_key }
                    return item
                })
            }
            })
            return data
        },
        async savePreset() {
            if (!this.form.name || !this.form.display_name || !this.form.definition.steps.length) {
                this.notify('保存失败', '请填写名称并添加至少一个步骤', 'warning')
                return
            }
            this.saving = true
            try {
                const data = this.serializeForm()
                const response = this.editing
                    ? await this.$api.scriptMgmt.updateOperationPreset(this.editing.id, data)
                    : await this.$api.scriptMgmt.createOperationPreset(data)
                if (!response.result) throw new Error(response.message || '保存失败')
                this.notify('快捷指令已保存', '', 'success')
                this.closeEditor()
                await this.load()
            } catch (error) {
                this.notify('保存失败', error.message, 'error')
            } finally {
                this.saving = false
            }
        },
        async copyPreset(preset) {
            try {
                const response = await this.$api.scriptMgmt.copyOperationPreset(preset.id)
                if (!response.result) throw new Error(response.message || '复制失败')
                this.notify('快捷指令已复制', '', 'success')
                await this.load()
            } catch (error) {
                this.notify('复制失败', error.message, 'error')
            }
        },
        async togglePreset(preset) {
            try {
                const method = preset.status === 'active'
                    ? 'deactivateOperationPreset'
                    : 'activateOperationPreset'
                const response = await this.$api.scriptMgmt[method](preset.id)
                if (!response.result) throw new Error(response.message || '操作失败')
                await this.load()
            } catch (error) {
                this.notify('操作失败', error.message, 'error')
            }
        },
        requestRemovePreset(preset) {
            this.presetToDelete = preset
            this.$nextTick(() => {
                this.$refs.deletePresetModal?.showModal()
            })
        },
        closeDeletePresetDialog() {
            if (this.$refs.deletePresetModal?.open) {
                this.$refs.deletePresetModal.close()
            } else {
                this.presetToDelete = null
            }
        },
        async confirmRemovePreset() {
            if (!this.presetToDelete || this.deletingPreset) return
            const preset = this.presetToDelete
            this.deletingPreset = true
            try {
                const response = await this.$api.scriptMgmt.deleteOperationPreset(preset.id)
                if (!response.result) throw new Error(response.message || '删除失败')
                this.notify('快捷指令已删除', '', 'success')
                this.closeDeletePresetDialog()
                await this.load()
            } catch (error) {
                this.notify('删除失败', error.message, 'error')
            } finally {
                this.deletingPreset = false
            }
        },
        notify(title, content, type) {
            this.$myMsg.notify({ title: this.$tt(title), content, type, time: 2500 })
        }
    }
}
</script>

<style scoped>
.preset-manager { min-height: 100%; padding: 14px; background: oklch(var(--b1)); color: oklch(var(--bc)); }
.preset-manager__toolbar, .preset-manager__tabs, .preset-card, .preset-editor__header, .preset-editor__footer { border: 1px solid oklch(var(--bc) / .1); background: oklch(var(--b2) / .72); }
.preset-manager__toolbar { display: flex; justify-content: space-between; align-items: center; gap: 16px; padding: 16px; border-radius: 12px; }
.preset-manager__toolbar h2 { margin: 0; font-size: 22px; font-weight: 800; }
.preset-manager__toolbar p { margin: 4px 0 0; color: oklch(var(--bc) / .58); font-size: 13px; }
.preset-manager__tabs { display: flex; align-items: center; gap: 5px; margin: 12px 0; padding: 5px; border-radius: 10px; }
.preset-manager__tabs button { border: 0; border-radius: 7px; padding: 8px 12px; color: oklch(var(--bc) / .65); background: transparent; cursor: pointer; }
.preset-manager__tabs button.active { color: oklch(var(--p)); background: oklch(var(--p) / .12); }
.preset-manager__tabs input { margin-left: auto; width: 220px; border: 1px solid oklch(var(--bc) / .1); border-radius: 7px; padding: 7px 10px; color: oklch(var(--bc)); background: oklch(var(--b1)); }
.preset-manager__grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 12px; }
.preset-card { min-height: 190px; padding: 15px; border-radius: 12px; transition: border-color .18s ease, opacity .18s ease; }
.preset-card--inactive { border-color: oklch(var(--bc) / .07); opacity: .7; }
.preset-card__header { display: flex; align-items: center; gap: 10px; }
.preset-card__icon { display: grid; width: 36px; height: 36px; place-items: center; border-radius: 9px; color: oklch(var(--p)); background: oklch(var(--p) / .12); }
.preset-card__title { min-width: 0; flex: 1; }
.preset-card__title h3 { margin: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 700; }
.preset-card__title span, .preset-card__description, .preset-card__meta { color: oklch(var(--bc) / .58); font-size: 12px; }
.preset-card__status { display: inline-flex; flex: 0 0 auto; align-items: center; gap: 5px; padding: 4px 7px; border: 1px solid transparent; border-radius: 6px; font-size: 11px; font-weight: 700; white-space: nowrap; }
.preset-card__status::before { width: 6px; height: 6px; border-radius: 50%; content: ''; }
.preset-card__status.is-active { border-color: oklch(var(--su) / .22); color: oklch(var(--su)); background: oklch(var(--su) / .11); }
.preset-card__status.is-active::before { background: oklch(var(--su)); box-shadow: 0 0 0 3px oklch(var(--su) / .12); }
.preset-card__status.is-inactive { border-color: oklch(var(--bc) / .1); color: oklch(var(--bc) / .52); background: oklch(var(--bc) / .06); }
.preset-card__status.is-inactive::before { background: oklch(var(--bc) / .42); }
.preset-card__description { min-height: 38px; margin: 14px 0; line-height: 1.6; }
.preset-card__meta, .preset-card__steps { display: flex; flex-wrap: wrap; gap: 8px; }
.preset-card__meta span, .preset-card__steps span { padding: 4px 7px; border-radius: 6px; background: oklch(var(--bc) / .06); }
.preset-card__steps { margin-top: 12px; color: oklch(var(--bc) / .78); font-size: 11px; }
.risk-medium { color: oklch(var(--a)); } .risk-high { color: oklch(var(--er)); } .risk-low { color: oklch(var(--su)); }
.preset-manager__empty { display: grid; place-items: center; gap: 10px; min-height: 220px; color: oklch(var(--bc) / .58); }
.preset-editor { display: flex; width: min(100vw, 560px); min-height: 100%; flex-direction: column; padding: 0 0 20px; color: oklch(var(--bc)); background: oklch(var(--b1)); }
.preset-editor__header, .preset-editor__footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 16px; }
.preset-editor__header h2 { margin: 0; font-size: 20px; font-weight: 800; }
.preset-editor__header p { margin: 4px 0 0; color: oklch(var(--bc) / .58); font-size: 12px; }
.preset-editor__body { display: grid; gap: 13px; padding: 16px; overflow-y: auto; }
.preset-editor__body .label-text { padding-bottom: 5px; color: oklch(var(--bc) / .72); font-size: 12px; }
.preset-editor__section-title, .preset-step__header { display: flex; align-items: center; gap: 8px; }
.preset-editor__section-title { justify-content: space-between; padding-top: 6px; border-top: 1px solid oklch(var(--bc) / .1); }
.preset-runtime-section { display: grid; gap: 8px; padding: 10px; border: 1px solid oklch(var(--bc) / .1); border-radius: 9px; background: oklch(var(--b2) / .38); }
.preset-runtime-section__hint { margin: 0; color: oklch(var(--bc) / .55); font-size: 12px; line-height: 1.5; }
.preset-runtime-field { display: grid; gap: 7px; padding: 9px; border: 1px solid oklch(var(--bc) / .08); border-radius: 7px; background: oklch(var(--b1) / .65); }
.preset-runtime-field__top, .preset-runtime-field__bottom { display: grid; grid-template-columns: 1fr 1fr auto; gap: 7px; align-items: center; }
.preset-runtime-field__bottom { grid-template-columns: 120px 1fr auto; }
.preset-runtime-field__required { display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; color: oklch(var(--bc) / .7); font-size: 12px; }
.preset-runtime-value { display: grid; grid-template-columns: minmax(0, 1fr) 170px; gap: 7px; }
.preset-step { display: grid; gap: 10px; padding: 11px; border: 1px solid oklch(var(--bc) / .1); border-radius: 9px; background: oklch(var(--b2) / .55); }
.preset-step__header > span { display: grid; width: 23px; height: 23px; place-items: center; border-radius: 50%; color: oklch(var(--p)); background: oklch(var(--p) / .12); font-size: 11px; }
.preset-step__header select { min-width: 0; flex: 1; }
.preset-step__params { display: grid; gap: 8px; }
.template-builder { display: flex; flex-wrap: wrap; gap: 5px; }
.template-builder { display: grid; gap: 8px; }
.template-parts { display: flex; min-height: 34px; flex-wrap: wrap; align-items: center; gap: 5px; padding: 6px; border: 1px solid oklch(var(--bc) / .1); border-radius: 7px; background: oklch(var(--b1) / .6); }
.template-part { display: inline-flex; align-items: center; gap: 4px; padding: 3px 6px; border-radius: 5px; color: oklch(var(--bc) / .78); background: oklch(var(--bc) / .08); font-size: 11px; }
.template-part button { padding: 0; border: 0; color: inherit; background: transparent; cursor: pointer; }
.template-builder__actions { display: flex; flex-wrap: wrap; align-items: center; gap: 5px; }
.template-builder__actions input { width: 110px; }
.tidy-fields { display: grid; gap: 7px; }
.preset-field-row { display: grid; grid-template-columns: 130px 1fr; align-items: center; gap: 8px; }
.preset-field-row label, .template-builder label { color: oklch(var(--bc) / .72); font-size: 12px; }
.preset-executions { display: flex; width: min(100vw, 540px); min-height: 100%; flex-direction: column; color: oklch(var(--bc)); background: oklch(var(--b1)); }
.preset-executions__header { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 16px; border-bottom: 1px solid oklch(var(--bc) / .1); }
.preset-executions__header h2 { margin: 0; font-size: 20px; font-weight: 800; }
.preset-executions__header p { margin: 4px 0 0; color: oklch(var(--bc) / .58); font-size: 12px; }
.preset-executions__body { display: grid; align-content: start; gap: 10px; padding: 16px; overflow-y: auto; }
.preset-execution { display: grid; gap: 10px; padding: 12px; border: 1px solid oklch(var(--bc) / .1); border-radius: 9px; background: oklch(var(--b2) / .55); }
.preset-execution__top, .preset-execution__counts { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 8px; }
.preset-execution__top span, .preset-execution__counts { color: oklch(var(--bc) / .58); font-size: 12px; }
.preset-execution__counts { justify-content: flex-start; }
.preset-execution__counts span { padding: 3px 6px; border-radius: 5px; background: oklch(var(--bc) / .06); }
.preset-execution__error { margin: 0; color: oklch(var(--er)); font-size: 12px; line-height: 1.5; }
.preset-execution details { color: oklch(var(--bc) / .72); font-size: 12px; }
.preset-execution pre { overflow: auto; max-height: 180px; margin: 8px 0 0; padding: 8px; border-radius: 6px; background: oklch(var(--b1)); font-size: 11px; white-space: pre-wrap; word-break: break-word; }
@media (max-width: 640px) {
    .preset-manager__toolbar, .preset-manager__tabs { align-items: stretch; flex-direction: column; }
    .preset-manager__tabs input { width: 100%; margin-left: 0; }
    .preset-manager__grid { grid-template-columns: 1fr; }
    .preset-field-row { grid-template-columns: 1fr; }
    .preset-runtime-field__top, .preset-runtime-field__bottom, .preset-runtime-value { grid-template-columns: 1fr; }
}
</style>
